import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  PendingActions as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ShoppingCart as ShoppingCartIcon,
  RemoveShoppingCart as NoPOIcon,
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';

interface SiteDashboardProps {
  projectId: number | null;
  access?: 'none' | 'read' | 'write';
}

interface MRSummary {
  total: number;
  draft: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  with_po: number;
  without_po: number;
}

interface MRTableRow {
  mr_serial_no: string;
  mr_description: string | null;
  status: string;
  vendor_name: string | null;
  date_of_issue: string | null;
  has_po: boolean;
  po_numbers: string[];
}

interface DCLocationStatus {
  document_location_id: number;
  location_name: string;
  pending: number;
  completed: number;
  na: number;
  not_assigned: number;
}

interface MaterialLocationStatus {
  document_location_id: number;
  location_name: string;
  pending: number;
  procured: number;
  na: number;
  not_assigned: number;
}

interface SubconLocationStatus {
  document_location_id: number;
  location_name: string;
  pending: number;
  assigned: number;
  na: number;
  not_assigned: number;
}

interface DashboardData {
  mr_summary: MRSummary;
  mr_table: MRTableRow[];
  dc_location_status: DCLocationStatus[];
  dc_location_totals: { pending: number; completed: number; na: number; not_assigned: number };
  material_location_status: MaterialLocationStatus[];
  material_location_totals: { pending: number; procured: number; na: number; not_assigned: number };
  subcon_location_status: SubconLocationStatus[];
  subcon_location_totals: { pending: number; assigned: number; na: number; not_assigned: number };
}

// Tile colors aligned with the rest of the app's status palette.
const TILE_COLORS = {
  draft: { bg: '#eef2ff', accent: '#6366f1', text: '#3730a3' },
  pending: { bg: '#fff7ed', accent: '#f59e0b', text: '#92400e' },
  approved: { bg: '#ecfdf5', accent: '#12b59f', text: '#065f46' },
  rejected: { bg: '#fef2f2', accent: '#ef4444', text: '#991b1b' },
  withPo: { bg: '#eff6ff', accent: '#2f4ae1', text: '#1e3a8a' },
  noPo: { bg: '#f9fafb', accent: '#6b7280', text: '#374151' },
};

const StatTile: React.FC<{
  label: string;
  value: number;
  Icon: React.ElementType;
  palette: { bg: string; accent: string; text: string };
  onClick?: () => void;
  selected?: boolean;
  dimmed?: boolean;
}> = ({ label, value, Icon, palette, onClick, selected = false, dimmed = false }) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      bgcolor: palette.bg,
      border: selected ? `2px solid ${palette.accent}` : `1px solid ${palette.accent}33`,
      borderRadius: 2,
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      // Dimmed = some other tile is selected; gray this one out so the
      // active filter stands out. Selected tiles get a stronger border
      // and a subtle ring to make the active state obvious.
      opacity: dimmed ? 0.45 : 1,
      filter: dimmed ? 'grayscale(0.5)' : 'none',
      transition: 'all 0.15s ease',
      boxShadow: selected ? `0 0 0 2px ${palette.accent}33` : 'none',
      '&:hover': onClick ? {
        boxShadow: selected
          ? `0 0 0 2px ${palette.accent}55`
          : `0 2px 8px ${palette.accent}33`,
        opacity: dimmed ? 0.7 : 1,
      } : {}
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: palette.text, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
          >
            {label}
          </Typography>
          <Typography variant="h4" sx={{ color: palette.text, fontWeight: 700, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: palette.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Filter keys for the clickable MR tiles. Status filters (draft / pending /
// approved / rejected) and PO filters (with_po / without_po) live in the
// same activeFilters set but apply as two AND-combined groups: an MR passes
// if it matches at least one selected status filter (or none selected) AND
// at least one selected PO filter (or none selected).
type MRFilterKey = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'with_po' | 'without_po';
const STATUS_FILTERS: MRFilterKey[] = ['draft', 'pending_approval', 'approved', 'rejected'];
const PO_FILTERS: MRFilterKey[] = ['with_po', 'without_po'];

const matchesStatusFilter = (mrStatus: string, key: MRFilterKey): boolean => {
  const s = (mrStatus || '').toLowerCase();
  switch (key) {
    case 'draft': return s === 'draft';
    case 'pending_approval': return s === 'pending approval';
    case 'approved': return s === 'approved';
    case 'rejected': return s === 'rejected';
    default: return false;
  }
};

const statusChipColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'error';
  if (s === 'pending approval') return 'warning';
  return 'default';
};

const SiteDashboard: React.FC<SiteDashboardProps> = ({ projectId, access = 'none' }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tile-based filters for the MR table. Empty set = no filter (show all).
  // Clicking a tile toggles its key in this set; Esc clears everything.
  const [activeFilters, setActiveFilters] = useState<Set<MRFilterKey>>(new Set());

  const toggleFilter = (key: MRFilterKey) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Esc clears all active filters. The listener is page-level so the user can
  // press Esc anywhere on the dashboard, matching the spec ("if user click
  // 'Esc' btm it will clear the filter").
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeFilters.size > 0) {
        setActiveFilters(new Set());
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeFilters.size]);

  useEffect(() => {
    if (access === 'none') return;
    if (!projectId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(apiUrl(`/api/site-dashboard?project_id=${projectId}`), {
      credentials: 'include',
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json.error || `Failed to load (${res.status})`);
        }
        return json;
      })
      .then((json: DashboardData) => {
        setData(json);
      })
      .catch((e: any) => {
        setError(e.message || 'Failed to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [projectId, access]);

  const recentMRs = useMemo(() => {
    if (!data) return [];
    // Apply active tile filters before sorting/slicing. Status filters and PO
    // filters combine as two AND-groups: an MR passes if it matches at least
    // one selected status (or no status selected) AND matches at least one
    // selected PO filter (or no PO filter selected). This lets the user mix
    // e.g. "Approved" + "With PO" to drill in.
    const activeStatus = STATUS_FILTERS.filter(k => activeFilters.has(k));
    const activePO = PO_FILTERS.filter(k => activeFilters.has(k));
    const filtered = data.mr_table.filter(mr => {
      const statusOK = activeStatus.length === 0
        || activeStatus.some(k => matchesStatusFilter(mr.status, k));
      const poOK = activePO.length === 0
        || activePO.some(k => (k === 'with_po') === mr.has_po);
      return statusOK && poOK;
    });
    // Most recent first by date_of_issue (nulls last), capped at 10 rows.
    return [...filtered]
      .sort((a, b) => {
        const da = a.date_of_issue || '';
        const db = b.date_of_issue || '';
        return db.localeCompare(da);
      })
      .slice(0, 10);
  }, [data, activeFilters]);

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to the Site Dashboard.</Alert>
      </Box>
    );
  }

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select a project from the sidebar to view the Site dashboard.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <>
          {/* MR Summary Tiles */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>
            Material Requisitions
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Click any tile to filter the MR table by that status / PO state.
                A tile is "selected" when its filter is active; if any filter is
                active, the other tiles dim to make the active selection obvious.
                Esc clears all filters (see effect above). */}
            <StatTile
              label="Draft"
              value={data.mr_summary.draft}
              Icon={DescriptionIcon}
              palette={TILE_COLORS.draft}
              onClick={() => toggleFilter('draft')}
              selected={activeFilters.has('draft')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('draft')}
            />
            <StatTile
              label="Pending Approval"
              value={data.mr_summary.pending_approval}
              Icon={PendingIcon}
              palette={TILE_COLORS.pending}
              onClick={() => toggleFilter('pending_approval')}
              selected={activeFilters.has('pending_approval')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('pending_approval')}
            />
            <StatTile
              label="Approved"
              value={data.mr_summary.approved}
              Icon={CheckCircleIcon}
              palette={TILE_COLORS.approved}
              onClick={() => toggleFilter('approved')}
              selected={activeFilters.has('approved')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('approved')}
            />
            <StatTile
              label="Rejected"
              value={data.mr_summary.rejected}
              Icon={CancelIcon}
              palette={TILE_COLORS.rejected}
              onClick={() => toggleFilter('rejected')}
              selected={activeFilters.has('rejected')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('rejected')}
            />
            <StatTile
              label="With PO"
              value={data.mr_summary.with_po}
              Icon={ShoppingCartIcon}
              palette={TILE_COLORS.withPo}
              onClick={() => toggleFilter('with_po')}
              selected={activeFilters.has('with_po')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('with_po')}
            />
            <StatTile
              label="Without PO"
              value={data.mr_summary.without_po}
              Icon={NoPOIcon}
              palette={TILE_COLORS.noPo}
              onClick={() => toggleFilter('without_po')}
              selected={activeFilters.has('without_po')}
              dimmed={activeFilters.size > 0 && !activeFilters.has('without_po')}
            />
          </Box>

          {/* Recent MR table with PO status */}
          <Card elevation={0} sx={{ mb: 4, border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#374151' }}>
                  Material Requisitions
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Showing {recentMRs.length} of {data.mr_table.length} total
                  {activeFilters.size > 0 && (
                    <Box component="span" sx={{ ml: 1, color: '#1976d2', fontWeight: 600 }}>
                      · Filtered ({activeFilters.size}) — press Esc to clear
                    </Box>
                  )}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>MR #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vendor</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>PO Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentMRs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Material Requisitions for this project yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {recentMRs.map((mr) => (
                      <TableRow key={mr.mr_serial_no} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{mr.mr_serial_no}</TableCell>
                        <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mr.mr_description || '—'}
                        </TableCell>
                        <TableCell>{mr.vendor_name || '—'}</TableCell>
                        <TableCell>{mr.date_of_issue || '—'}</TableCell>
                        <TableCell>
                          <Chip label={mr.status} size="small" color={statusChipColor(mr.status)} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell>
                          {mr.po_numbers && mr.po_numbers.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {mr.po_numbers.map((po) => (
                                <Chip
                                  key={po}
                                  label={po}
                                  size="small"
                                  color="primary"
                                  icon={<ShoppingCartIcon />}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Chip label="No PO" size="small" variant="outlined" onClick={(e) => e.stopPropagation()} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* DC Status by Document Location */}
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#374151' }}>
                  Design Change Status by Document Location
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Counts of Design Change ↔ Location pairs per status
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Document Location</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>Pending</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#065f46' }}>Completed</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>N/A</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>Not Assigned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.dc_location_status.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Document Locations configured. Add them under Design Change → Document Settings → Document Locations.
                        </TableCell>
                      </TableRow>
                    )}
                    {data.dc_location_status.map((loc) => (
                      <TableRow key={loc.document_location_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{loc.location_name}</TableCell>
                        <TableCell align="center">
                          <Chip label={loc.pending} size="small" sx={{ bgcolor: '#fff7ed', color: '#92400e', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.completed} size="small" sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.na} size="small" sx={{ bgcolor: '#f3f4f6', color: '#374151', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.not_assigned} size="small" sx={{ bgcolor: '#fef2f2', color: '#7c2d12', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.dc_location_status.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>{data.dc_location_totals.pending}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#065f46' }}>{data.dc_location_totals.completed}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>{data.dc_location_totals.na}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>{data.dc_location_totals.not_assigned}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Material Status by Document Location */}
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mt: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#374151' }}>
                  Material Status by Document Location
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Counts of Design Change ↔ Location pairs by Material status
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Document Location</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>Pending</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#1e3a8a' }}>Procured</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>N/A</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>Not Assigned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.material_location_status.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Document Locations configured.
                        </TableCell>
                      </TableRow>
                    )}
                    {data.material_location_status.map((loc) => (
                      <TableRow key={loc.document_location_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{loc.location_name}</TableCell>
                        <TableCell align="center">
                          <Chip label={loc.pending} size="small" sx={{ bgcolor: '#fff7ed', color: '#92400e', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.procured} size="small" sx={{ bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.na} size="small" sx={{ bgcolor: '#f3f4f6', color: '#374151', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.not_assigned} size="small" sx={{ bgcolor: '#fef2f2', color: '#7c2d12', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.material_location_status.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>{data.material_location_totals.pending}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#1e3a8a' }}>{data.material_location_totals.procured}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>{data.material_location_totals.na}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>{data.material_location_totals.not_assigned}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Subcon Status by Document Location */}
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mt: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #eef0f3' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#374151' }}>
                  Subcon Status by Document Location
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Counts of Design Change ↔ Location pairs by Subcon status
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Document Location</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>Pending</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#065f46' }}>Assigned</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>N/A</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>Not Assigned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.subcon_location_status.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#9ca3af', py: 3 }}>
                          No Document Locations configured.
                        </TableCell>
                      </TableRow>
                    )}
                    {data.subcon_location_status.map((loc) => (
                      <TableRow key={loc.document_location_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{loc.location_name}</TableCell>
                        <TableCell align="center">
                          <Chip label={loc.pending} size="small" sx={{ bgcolor: '#fff7ed', color: '#92400e', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.assigned} size="small" sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.na} size="small" sx={{ bgcolor: '#f3f4f6', color: '#374151', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={loc.not_assigned} size="small" sx={{ bgcolor: '#fef2f2', color: '#7c2d12', fontWeight: 600 }} onClick={(e) => e.stopPropagation()} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.subcon_location_status.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f9fafb' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#92400e' }}>{data.subcon_location_totals.pending}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#065f46' }}>{data.subcon_location_totals.assigned}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#6b7280' }}>{data.subcon_location_totals.na}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#7c2d12' }}>{data.subcon_location_totals.not_assigned}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default SiteDashboard;
